<template>
    <li
        :class="notification && notificationHasLink ? 'cursor-pointer' : ''"
        :data-type="type"
        class="w-full px-24 pt-12 notifications-item"
        @click="goToNotification"
    >
        <div
            class="rounded-6 shadow pointer-events-auto relative bg-white p-16"
            :class="[borderClass]"
            @mouseleave="isPaused = false"
            @mouseover="isPaused = true"
        >
            <div class="flex items-start justify-between relative z-20">
                <div class="flex items-start gap-12">
                    <svg-icon :class="iconColor" :type="icon" class="w-18 h-18" />
                    <div class="text-neutral-black">
                        <p class="font-semibold">{{ notificationTitle }}</p>
                        <p v-html="msg"></p>
                        <simple-button
                            v-if="notification && notificationHasLink"
                            look="text"
                            :display="type"
                            capitalize
                        >
                            {{ $t('general.view') }}
                        </simple-button>
                    </div>
                </div>
                <i @click="close">
                    <svg-icon type="cancel" class="w-18 h-18 fill-neutral-black cursor-pointer ms-auto" />
                </i>
            </div>
        </div>
    </li>
</template>

<script>
import {DELETE_NOTIFICATION} from '../../state/mutation-types';
import SvgIcon from '../ui/SvgIcon.vue';
import notifications from '../../mixins/notifications';
import SimpleButton from "../ui/SimpleButton.vue";
import {mapStores, mapActions} from "pinia";
import {useAuthenticationStore} from "../../state/modules/authentication";
import {useNotificationsStore} from '../../state/modules/notifications';

export default {
    mixins: [notifications],
    components: {
        SimpleButton,
        'svg-icon': SvgIcon,
    },
    props: {
        id: String,
        type: {
            default: 'notification',
            validator: value => ['error', 'warning', 'notification', 'success', 'neutral'].includes(value),
        },
        message: String,
        title: String,
        notification: Object,
    },
    data: () => ({
        timer: null,
        progress: 100,
        isPaused: false,
    }),
    computed: {
        ...mapStores(useAuthenticationStore),
        msg() {
            if (this.type === 'error') {
                return this.$t(this.message);
            }

            return this.message;
        },
        borderClass() {
            return {
                error: 'border-l-6 border-semantic-error',
                warning: 'border-l-6 border-semantic-warning',
                success: 'border-l-6 border-semantic-success',
                notification: 'border-l-6 border-semantic-info',
                neutral: 'border-l-6 border-primary-40',
            }[this.type]
        },
        bgClass() {
            return {
                error: 'bg-semantic-error',
                warning: 'bg-semantic-warning',
                notification: 'bg-semantic-info',
                success: 'bg-semantic-success',
                neutral: 'bg-primary-40',
            }[this.type];
        },
        icon() {
            return {
                error: 'error-filled',
                warning: 'warning-filled',
                notification: 'info-filled',
                success: 'check-circle-filled',
                neutral: 'light-bulb-filled',
            }[this.type];
        },
        iconColor() {
            return {
                error: 'text-semantic-error',
                warning: 'text-semantic-warning',
                notification: 'text-semantic-info',
                success: 'text-semantic-success',
                neutral: 'text-primary-40',
            }[this.type];
        },
        notificationTitle() {
            return this.title ?? {
                error: this.$t('notifications.error'),
                warning: this.$t('notifications.warning'),
                notification: this.$t('notifications.info'),
                success: this.$t('notifications.success'),
                neutral: '',
            }[this.type];
        },
        notificationHasLink() {
            return this.notification.data?.hasOwnProperty('application') || this.notification.data?.hasOwnProperty('job')
        }
    },
    methods: {
        ...mapActions(useNotificationsStore, [DELETE_NOTIFICATION]),
        close: function () {
            clearInterval(this.timer);
            this[DELETE_NOTIFICATION](this.id);
        },
        goToNotification() {
            if (!this.notification || !this.notificationHasLink) {
                return;
            }

            this.visitNotificationSource(this.notification);
            this.close();
        },
    },
    mounted: function () {
        let time = 5000;

        if (this.message.length >= 100) {
            time = 10000;
        }

        this.timer = setInterval(() => {
            if (!this.isPaused) {
                if (this.progress <= 0) {
                    this.close(this.id);
                    clearInterval(this.timer);
                } else {
                    this.progress--;
                }
            }
        }, time / 100);
    },
};
</script>
